import React, { useState, useEffect, useMemo } from 'react';
import {
    Card, Typography, Row, Col, Button, List, Tag, Switch,
    message, Spin, Space, Modal, Form, Input, Collapse
} from 'antd';
import {
    KeyOutlined, SafetyOutlined, SaveOutlined,
    PlusOutlined, DeleteOutlined, ExclamationCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { useAuth } from '../../../aws/AuthContext';

const { Title, Text } = Typography;
const { confirm } = Modal;

// --- DICCIONARIO PARA NOMBRES AMIGABLES DE PERMISOS ---
const getFriendlyName = (clave) => {
    const labels = {
        // Ventas
        'ventas:ver': 'Acceso a la terminal de ventas',
        'ventas:editar_precio': 'Editar precios en POS',
        'ventas:historial_ventas': 'Ver historial de ventas',
        // Ruta de entredas
        'ruta:ver': 'Acceso al módulo Mi Ruta de Entregas',
        // Pedidos
        'pedidos:ver': 'Acceso al módulo de Pedidos',
        'pedidos:crear': 'Tomar pedidos en calle (Móvil / Vendedor de Ruta)',
        'pedidos:gestionar': 'Aprobar y facturar pedidos (Despacho / Bodega)',
        // Caja
        'caja:ver': 'Acceso al cierre de caja',
        // Facturación
        'facturacion:ver': 'Acceso al módulo de facturación y documentos',
        // Productos
        'productos:ver': 'Acceso al módulo de productos',
        'productos:crear': 'Crear producto',
        'productos:precio_compra': 'Ocultar precio de compra',
        'productos:editar': 'Editar producto',
        'productos:desactivar': 'Desactivar productos',
        // Inventario
        'inventario:ver': 'Acceso al módulo Inventario',
        'inventario:crear': 'Crear nuevos productos',
        'inventario:editar': 'Modificar descuento de productos',
        'inventario:eliminar': 'Desactivar producto',
        'inventario:costos': 'Ver precio de compra',
        // Compras
        'compras:ver': 'Acceso al módulo compras',
        'compras:crear': 'Registrar Compras',
        // Proveedores
        'proveedores:ver': 'Acceso al módulo de proveedores',
        'proveedores:crear': 'Crear nuevo proveedor',
        'proveedores:editar': 'Modificar proveedores',
        'proveedores:eliminar': 'Eliminar proveedor',
        // Informes
        'informes:ver': 'Acceso al módulo de Informes',
        'informes:resumen_general': 'Ver resumen general',
        'informes:ver_ventas': 'Ver ventas',
        'informes:ver_gastos': 'Ver Gastos',
        // Gastos
        'gastos:ver': 'Acceso al módulo de gastos',
        'gastos:crear_editar_categoria': 'Crear o editar categoria',
        'gastos:crear': 'Registrar gastos',
        'gastos:editar': 'Editar facturas de gastos',
        'gastos:eliminar': 'Eliminar gastos',
        // Clientes
        'clientes:ver': 'Acceso al módulo de clientes',
        'clientes:ver_todos': 'Ver la totalidad de clientes de la empresa',
        'clientes:exportar': 'Descargar base de datos de clientes (Excel/CSV)',
        'clientes:configurar_campos': 'Configurar campos obligatorios del formulario',
        // Ajustes
        'configuracion:ver': 'Acceso al módulo de ajustes',
        'configuracion:ver_administracion': 'Ver sesión Administración',
        'configuracion:ver_seguridad': 'Ver sesión Seguridad',
        'configuracion:ver_punto_venta': 'Ver sesión Punto de Venta',
        'configuracion:ver_servicios': 'Ver sesión Catálogo de Servicios',
        'configuracion:ver_personalizacion': 'Ver sesión Personalización',
    };

    if (labels[clave]) return labels[clave];

    const [modulo, accion] = clave.split(':');
    if (accion === 'ver') return `Acceso a ${modulo}`;
    if (accion === 'gestionar') return `Control total de ${modulo}`;
    if (accion === 'crear') return `Agregar ${modulo}`;
    if (accion === 'editar') return `Modificar ${modulo}`;
    if (accion === 'eliminar') return `Desactivar/Eliminar ${modulo}`;
    return clave;
};

const SeccionRoles = () => {
    const { getJwtToken, fetchFullProfile } = useAuth();
    const API_URL = process.env.REACT_APP_API_URL;

    // --- 1. HOOKS DE ESTADO ---
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [roles, setRoles] = useState([]);
    const [catalogo, setCatalogo] = useState([]);
    const [asignaciones, setAsignaciones] = useState([]);
    const [rolSeleccionado, setRolSeleccionado] = useState(null);
    const [permisosTemporales, setPermisosTemporales] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [form] = Form.useForm();

    const handleEliminarRol = (rolId, nombre) => {
        if (nombre === 'CEO') return message.warning("El rol CEO no puede ser eliminado.");

        confirm({
            title: `¿Eliminar el rol "${nombre}"?`,
            icon: <ExclamationCircleOutlined style={{ color: 'red' }} />,
            content: 'Se quitará este rol de todos los usuarios asignados.',
            okText: 'Eliminar',
            okType: 'danger',
            cancelText: 'Cancelar',
            onOk: async () => {
                try {
                    const token = await getJwtToken();
                    await axios.delete(`${API_URL}/v1-roles-permisos/${rolId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    message.success("Rol eliminado");
                    fetchData();
                } catch (error) {
                    message.error("Error al eliminar el rol");
                }
            },
        });
    };

    // --- 2. CARGA DE DATOS ---
    const fetchData = async () => {
        setLoading(true);
        try {
            const token = await getJwtToken();
            const { data } = await axios.get(`${API_URL}/v1-roles-permisos`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            const rolesOrdenados = (data.roles || []).sort((a, b) => {
                if (a.nombre === 'CEO') return -1;
                if (b.nombre === 'CEO') return 1;
                return a.nombre.localeCompare(b.nombre);
            });

            setRoles(rolesOrdenados);
            setCatalogo(data.catalogo || []);
            setAsignaciones(data.asignaciones || []);

            // Si hay un rol ya seleccionado, refrescamos sus permisos, sino seleccionamos el primero
            const rolObjetivo = rolSeleccionado || rolesOrdenados[0];
            if (rolObjetivo) {
                setRolSeleccionado(rolObjetivo);
                const claves = (data.asignaciones || [])
                    .filter(a => a.rol_id === rolObjetivo.id)
                    .map(a => a.clave);
                setPermisosTemporales(claves);
            }
        } catch (error) {
            message.error("Error al sincronizar roles");
        } finally {
            setLoading(false);
        }
    };

    const handleCrearRol = async (values) => {
        try {
            const token = await getJwtToken();
            await axios.post(`${API_URL}/v1-roles-permisos`, {
                nombre: values.nombre,
                permisos_claves: []
            }, { headers: { Authorization: `Bearer ${token}` } });

            message.success("Rol creado exitosamente.");
            setIsModalOpen(false);
            form.resetFields();
            fetchData();
        } catch (error) {
            message.error("Error al crear el rol");
        }
    };

    useEffect(() => { fetchData(); }, []);

    // --- 3. TRANSFORMACIÓN DE DATOS (AGRUPACIÓN POR MÓDULOS) ---
    const modulosAgrupados = useMemo(() => {
        const groups = {};
        catalogo.forEach(p => {
            if (!groups[p.modulo]) {
                groups[p.modulo] = {
                    key: p.modulo,
                    nombre: p.modulo.charAt(0).toUpperCase() + p.modulo.slice(1),
                    permisos: []
                };
            }
            groups[p.modulo].permisos.push(p);
        });

        // 1. EL SECRETO DEL ORDEN: Definimos qué acción va primero
        const ORDEN_ACCIONES = ['ver', 'gestionar', 'crear', 'editar', 'eliminar', 'costos'];

        Object.values(groups).forEach(g => {
            g.permisos.sort((a, b) => {
                const accionA = a.clave.split(':')[1];
                const accionB = b.clave.split(':')[1];

                let indexA = ORDEN_ACCIONES.indexOf(accionA);
                let indexB = ORDEN_ACCIONES.indexOf(accionB);

                if (indexA === -1) indexA = 99; // Si no está en la lista, va al final
                if (indexB === -1) indexB = 99;

                return indexA - indexB;
            });
        });

        // Definimos el orden exacto de los paneles (Módulos)
        const ORDEN_DESEADO = [
            'ventas', 'ruta', 'pedidos', 'caja', 'facturacion', 'productos', 'inventario', 'compras',
            'proveedores', 'informes', 'gastos', 'clientes', 'usuarios', 'configuración'
        ];

        // Ordenamos los módulos
        const modulosArray = Object.values(groups);
        modulosArray.sort((a, b) => {
            let indexA = ORDEN_DESEADO.indexOf(a.key.toLowerCase());
            let indexB = ORDEN_DESEADO.indexOf(b.key.toLowerCase());

            if (indexA === -1) indexA = 999;
            if (indexB === -1) indexB = 999;

            return indexA - indexB;
        });

        return modulosArray;
    }, [catalogo]);

    const seleccionarRol = (rol) => {
        setRolSeleccionado(rol);
        const claves = asignaciones.filter(a => a.rol_id === rol.id).map(a => a.clave);
        setPermisosTemporales(claves);
    };

    const handleTogglePermisoInteligente = (clave) => {
        if (!clave) return;
        const [modulo, accion] = clave.split(':');

        setPermisosTemporales(prev => {
            const tienePermiso = prev.includes(clave);
            let nuevosPermisos = new Set(prev);

            if (tienePermiso) {
                // APAGAR INTERRUPTOR
                nuevosPermisos.delete(clave);

                // Si apaga "Ver", se apagan automáticamente los permisos secundarios de ese módulo
                if (accion === 'ver' || accion === 'gestionar') {
                    Array.from(nuevosPermisos).forEach(p => {
                        if (p.startsWith(`${modulo}:`)) nuevosPermisos.delete(p);
                    });
                }
            } else {
                // ENCENDER INTERRUPTOR
                nuevosPermisos.add(clave);

                // Si enciende "Crear" o "Gestionar", activa automáticamente "Ver"
                if (accion !== 'ver') {
                    nuevosPermisos.add(`${modulo}:ver`);
                }
            }
            return Array.from(nuevosPermisos);
        });
    };

    const handleGuardarPermisos = async () => {
        setSaving(true);
        try {
            const token = await getJwtToken();
            await axios.put(`${API_URL}/v1-roles-permisos`, {
                id: rolSeleccionado.id,
                nombre: rolSeleccionado.nombre,
                permisos_claves: permisosTemporales
            }, { headers: { Authorization: `Bearer ${token}` } });
    
            message.success("Configuración de permisos guardada correctamente.");
            await fetchData();
    
            // 2. Si el usuario actual actualizó el rol que tiene asignado (ej: CEO),
            // recargamos suavemente sus permisos en memoria sin recargar la página.
            if (typeof fetchFullProfile === 'function') {
                await fetchFullProfile();
            }
        } catch (error) {
            console.error("Error al guardar permisos:", error);
            message.error("Error al guardar permisos");
        } finally {
            setSaving(false);
        }
    };

    if (loading) return (
        <div style={{ textAlign: 'center', padding: '100px' }}>
            <Spin size="large" tip="Cargando estructura de seguridad..." />
        </div>
    );

    return (
        <div className="fade-in">
            <Row gutter={[16, 16]}>
                {/* COLUMNA IZQUIERDA: LISTA DE ROLES */}
                <Col xs={24} md={8} lg={7}>
                    <Card
                        title="Roles del Sistema"
                        size="small"
                        extra={<Button type="link" size="small" icon={<PlusOutlined />} onClick={() => setIsModalOpen(true)}>Añadir</Button>}
                        styles={{ body: { padding: '8px' }, header: { minHeight: '40px' } }}
                    >
                        <List
                            dataSource={roles}
                            size="small"
                            renderItem={item => (
                                <List.Item
                                    onClick={() => seleccionarRol(item)}
                                    style={{
                                        cursor: 'pointer',
                                        padding: '8px 12px',
                                        borderRadius: '6px',
                                        marginBottom: '4px',
                                        border: rolSeleccionado?.id === item.id ? '1px solid #22BABB' : '1px solid transparent',
                                        backgroundColor: rolSeleccionado?.id === item.id ? '#f0f9f9' : 'transparent',
                                        transition: 'all 0.3s'
                                    }}
                                >
                                    <Space>
                                        <SafetyOutlined style={{ color: item.nombre === 'CEO' ? '#faad14' : '#22BABB', fontSize: '16px' }} />
                                        <Text strong style={{ fontSize: '13px' }}>{item.nombre}</Text>
                                    </Space>
                                    {item.nombre !== 'CEO' && (
                                        <Button
                                            type="text" danger size="small" icon={<DeleteOutlined />}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleEliminarRol(item.id, item.nombre);
                                            }}
                                        />
                                    )}
                                </List.Item>
                            )}
                        />
                    </Card>
                </Col>

                {/* COLUMNA DERECHA: CONFIGURACIÓN POR MÓDULOS */}
                <Col xs={24} md={16} lg={17}>
                    <Card
                        size="small"
                        title={<Space><KeyOutlined /> Permisos por Módulos: <Tag color="blue" style={{ margin: 0 }}>{rolSeleccionado?.nombre}</Tag></Space>}
                        extra={<Button type="primary" size="small" icon={<SaveOutlined />} onClick={handleGuardarPermisos} loading={saving} style={{ backgroundColor: '#22BABB', border: 'none' }}>Guardar</Button>}
                        styles={{ body: { padding: '12px' } }}
                    >
                        <Collapse
                            defaultActiveKey={modulosAgrupados.map(m => m.key)}
                            ghost
                            size="small"
                            expandIconPosition="end"
                        >
                            {modulosAgrupados.map(grupo => (
                                <Collapse.Panel
                                    key={grupo.key}
                                    header={<Text strong>{grupo.nombre}</Text>}
                                    style={{ borderBottom: '1px solid #f0f0f0' }}
                                >
                                    <List
                                        size="small"
                                        dataSource={grupo.permisos}
                                        renderItem={permiso => {
                                            const isCEO = rolSeleccionado?.nombre === 'CEO';
                                            
                                            // 1. Alineamos las excepciones visuales exactamente con las de usePermission.js
                                            const esPermisoExcepcionCEO = permiso.modulo === 'pedidos' || 
                                                                          permiso.modulo === 'ruta' || 
                                                                          permiso.clave === 'clientes:ver_todos';
                                        
                                            // 2. Si es CEO y NO es una excepción, forzamos visualmente a que se vea encendido 
                                            // porque el CEO ya tiene acceso implícito a todo lo demás (gastos, exportar clientes, etc.)
                                            const isChecked = (isCEO && !esPermisoExcepcionCEO) 
                                                ? true 
                                                : permisosTemporales.includes(permiso.clave);
                                        
                                            // 3. Bloqueamos el botón para el CEO (excepto en las excepciones donde sí debe decidir)
                                            const isDisabled = isCEO && !esPermisoExcepcionCEO;
                                        
                                            return (
                                                <List.Item
                                                    style={{ padding: '6px 0', border: 'none' }}
                                                    extra={
                                                        <Switch
                                                            size="small"
                                                            checked={isChecked}
                                                            onChange={() => handleTogglePermisoInteligente(permiso.clave)}
                                                            disabled={isDisabled}
                                                            style={{ backgroundColor: isChecked ? '#22BABB' : undefined }}
                                                        />
                                                    }
                                                >
                                                    <Text style={{ fontSize: '13px', color: isDisabled ? '#bfbfbf' : 'inherit' }}>
                                                        {getFriendlyName(permiso.clave)}
                                                    </Text>
                                                </List.Item>
                                            );
                                        }}
                                    />
                                </Collapse.Panel>
                            ))}
                        </Collapse>
                    </Card>
                </Col>
            </Row>

            <Modal title="Crear Nuevo Rol" open={isModalOpen} onCancel={() => setIsModalOpen(false)} onOk={() => form.submit()} forceRender centered>
                <Form form={form} layout="vertical" onFinish={handleCrearRol}>
                    <Form.Item name="nombre" label="Nombre del Rol" rules={[{ required: true, message: 'Requerido. Ej: Cajero' }]}>
                        <Input placeholder="Ej: Administrador Nocturno" size="large" />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default SeccionRoles;